import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import {
  IconLoader,
  ButtonPrimary,
  SidePanel,
  SidePanelType,
  SidePanelLoader,
  Currency,
  ButtonPseudo,
  responseErrorsResolver,
  ResponseError,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetExchangePaymentTypesQuery,
  useLazyGetPaymentTypeQuery,
  useEditPaymentTypeCurrenciesMutation,
  usePaymentTypeStatusesBatchUpdateMutation,
} from './store/services/payment-types';
import PaymentTypeItem from '../../components/settings/payment-types/payment-type';
import PaymentTypeForm from '../../components/settings/payment-types/payment-type-form';
import {
  updatePaymentTypes,
  clearPaymentTypeState,
  setIsSuccess,
} from './store/slices/payment-types';
import { useLazyGetActiveCurrenciesQuery } from '../../store/dictionaries/service';
import { PaymentType } from './types';
import { PermissionEnum } from '../user-roles/types';

import cn from './styles.module.css';

const PaymentTypes = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState('');
  const [updatedPaymentTypes, setUpdatedPaymentTypes] = useState<
    {
      id: string;
      visibleToClients: boolean;
      enabled: boolean;
    }[]
  >([]);
  const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>(
    [],
  );
  const [defaultAvailableCurrencies, setDefaultAvailableCurrencies] = useState<
    Currency[]
  >([]);
  const [globalError, setGlobalError] = useState('');
  const [errorList, setErrorList] = useState('');

  const {
    settings: {
      paymentTypes: { paymentTypes, paymentType, isSuccess: updateIsSuccess },
    },
    administrationDictionaries: { activeCurrencies, activePermissions },
  } = useAppSelector((state) => state);

  const [getActiveCurrencies] = useLazyGetActiveCurrenciesQuery();

  const dispatch = useAppDispatch();

  const [getExchangePaymentTypes, { isLoading }] =
    useLazyGetExchangePaymentTypesQuery();
  const [getPaymentType, { isLoading: typeIsLoading, isFetching }] =
    useLazyGetPaymentTypeQuery();
  const [editPaymentTypeCurrencies, { isLoading: requestInProgress }] =
    useEditPaymentTypeCurrenciesMutation();
  const [
    paymentTypeStatusesBatchUpdate,
    { isLoading: batchUpdateInProgress, isSuccess },
  ] = usePaymentTypeStatusesBatchUpdateMutation();

  const onBatchStatusesUpdated = async () => {
    await paymentTypeStatusesBatchUpdate({
      data: updatedPaymentTypes,
    });
  };

  const onEditPaymentTypeCurrencies = async () => {
    setGlobalError('');

    const response = (await editPaymentTypeCurrencies({
      id: selectedPaymentType,
      body: {
        id: selectedPaymentType,
        currencies: availableCurrencies?.map((item) => item.id),
      },
    })) as { error?: ResponseError };

    if (response) {
      responseErrorsResolver({
        errors: response.error as ResponseError,
        setError: () => {},
        setGlobalError,
      });
    }
  };

  const onUpdatePaymentTypes = (paymentTypeValue: PaymentType) => {
    const nextPaymentTypes = paymentTypes?.map((item) => {
      let result = { ...item };

      if (result.id === paymentTypeValue.id) {
        result = paymentTypeValue;
      }

      return result;
    });

    dispatch(updatePaymentTypes(nextPaymentTypes as PaymentType[]));
  };

  const handleClose = () => {
    setPanelIsOpen(false);
    setSelectedPaymentType('');
    setAvailableCurrencies([]);
    setGlobalError('');

    dispatch(clearPaymentTypeState());
  };

  const getExchangePaymentTypesAction = useCallback(async () => {
    const response = (await getExchangePaymentTypes()) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  useEffect(() => {
    if (!activeCurrencies) {
      getActiveCurrencies();
    }
  }, [activeCurrencies]);

  useEffect(() => {
    if (!paymentTypes) {
      getExchangePaymentTypesAction();
    }
  }, [paymentTypes]);

  useEffect(() => {
    if (updateIsSuccess) {
      dispatch(setIsSuccess());
    }
  }, [updateIsSuccess]);

  useEffect(() => {
    if (isSuccess) {
      setUpdatedPaymentTypes([]);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (panelIsOpen && selectedPaymentType) {
      getPaymentType(selectedPaymentType);
    }
  }, [panelIsOpen, selectedPaymentType]);

  useEffect(() => {
    if (paymentType) {
      setAvailableCurrencies(
        paymentType?.currencies?.edges?.map((item) => item.node),
      );

      setDefaultAvailableCurrencies(
        paymentType?.currencies?.edges?.map((item) => item.node),
      );
    }
  }, [paymentType]);

  useEffect(() => {
    let title = 'Transaction Settings: Payment types';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;
  let sidepanelContent;

  if (typeIsLoading || isFetching) {
    sidepanelContent = <SidePanelLoader />;
  } else {
    sidepanelContent = (
      <PaymentTypeForm
        availableCurrencies={availableCurrencies}
        setAvailableCurrencies={setAvailableCurrencies}
      />
    );
  }

  if (isLoading) {
    content = (
      <div className={cn.paymentTypesLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <>
        {errorList && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}
        {!errorList &&
          paymentTypes?.map((item) => (
            <PaymentTypeItem
              key={item.id}
              paymentType={item}
              updatedPaymentTypes={updatedPaymentTypes}
              setUpdatedPaymentTypes={setUpdatedPaymentTypes}
              setSelectedPaymentType={setSelectedPaymentType}
              onUpdatePaymentTypes={onUpdatePaymentTypes}
              setPanelIsOpen={setPanelIsOpen}
              isSuccess={isSuccess}
              editAvailable={activePermissions?.includes(
                PermissionEnum.SETTINGS_SYSTEM_EDIT_ACTION,
              )}
            />
          ))}
      </>
    );
  }

  return (
    <div className={cn.paymentTypesWrapper}>
      <div className={cn.paymentTypesTitle}>Availability of payment types</div>
      {content}
      {!errorList && (
        <ButtonPrimary
          text={batchUpdateInProgress ? 'Loading...' : 'Save'}
          className={cn.saveButton}
          onClick={onBatchStatusesUpdated}
          disabled={!updatedPaymentTypes.length || batchUpdateInProgress}
          bold
        />
      )}

      <SidePanel
        title="Available currencies"
        isOpen={panelIsOpen}
        type={SidePanelType.AML}
        onClose={handleClose}
        errorSidepanel={globalError}
        sidepanelExpandedKey="payment-types-currencies"
        footer={
          <>
            <ButtonPrimary
              text={requestInProgress ? 'Loading...' : 'Save'}
              disabled={
                isEqual(defaultAvailableCurrencies, availableCurrencies) ||
                requestInProgress ||
                !activePermissions?.includes(
                  PermissionEnum.SETTINGS_SYSTEM_EDIT_ACTION,
                )
              }
              onClick={onEditPaymentTypeCurrencies}
              bold
            />
            <ButtonPseudo text="Close" onClick={handleClose} bold />
          </>
        }
        closeOnClickOutside
      >
        {sidepanelContent}
      </SidePanel>
    </div>
  );
};

export default PaymentTypes;
